/**
 * ISO 3166-1 sovereign states, bucketed into the seven regions the picker walks.
 * Names come from `Intl.DisplayNames`; territories and dependencies are left out.
 *
 * Those omissions and the spelling differences against the product's own list are
 * reconciled by `countryNameByCode` / `countryCodeByName` at the foot of this file.
 * Read that before mapping a country anywhere else.
 */
import { CountriesList } from 'config/countries'

export interface Country {
	/** ISO 3166-1 alpha-2. Unique globally, not just inside its continent. */
	id: string
	label: string
}

export interface Continent {
	id: string
	label: string
	countries: Country[]
}

export const CONTINENTS: Continent[] = [
	{
		id: 'africa',
		label: 'Africa',
		countries: [
			{ id: 'DZ', label: 'Algeria' },
			{ id: 'AO', label: 'Angola' },
			{ id: 'BJ', label: 'Benin' },
			{ id: 'BW', label: 'Botswana' },
			{ id: 'BF', label: 'Burkina Faso' },
			{ id: 'BI', label: 'Burundi' },
			{ id: 'CM', label: 'Cameroon' },
			{ id: 'CV', label: 'Cape Verde' },
			{ id: 'CF', label: 'Central African Republic' },
			{ id: 'TD', label: 'Chad' },
			{ id: 'KM', label: 'Comoros' },
			{ id: 'CG', label: 'Congo - Brazzaville' },
			{ id: 'CD', label: 'Congo - Kinshasa' },
			{ id: 'CI', label: 'Côte d’Ivoire' },
			{ id: 'DJ', label: 'Djibouti' },
			{ id: 'EG', label: 'Egypt' },
			{ id: 'GQ', label: 'Equatorial Guinea' },
			{ id: 'ER', label: 'Eritrea' },
			{ id: 'SZ', label: 'Eswatini' },
			{ id: 'ET', label: 'Ethiopia' },
			{ id: 'GA', label: 'Gabon' },
			{ id: 'GM', label: 'Gambia' },
			{ id: 'GH', label: 'Ghana' },
			{ id: 'GN', label: 'Guinea' },
			{ id: 'GW', label: 'Guinea-Bissau' },
			{ id: 'KE', label: 'Kenya' },
			{ id: 'LS', label: 'Lesotho' },
			{ id: 'LR', label: 'Liberia' },
			{ id: 'LY', label: 'Libya' },
			{ id: 'MG', label: 'Madagascar' },
			{ id: 'MW', label: 'Malawi' },
			{ id: 'ML', label: 'Mali' },
			{ id: 'MR', label: 'Mauritania' },
			{ id: 'MU', label: 'Mauritius' },
			{ id: 'MA', label: 'Morocco' },
			{ id: 'MZ', label: 'Mozambique' },
			{ id: 'NA', label: 'Namibia' },
			{ id: 'NE', label: 'Niger' },
			{ id: 'NG', label: 'Nigeria' },
			{ id: 'RW', label: 'Rwanda' },
			{ id: 'ST', label: 'São Tomé & Príncipe' },
			{ id: 'SN', label: 'Senegal' },
			{ id: 'SC', label: 'Seychelles' },
			{ id: 'SL', label: 'Sierra Leone' },
			{ id: 'SO', label: 'Somalia' },
			{ id: 'ZA', label: 'South Africa' },
			{ id: 'SS', label: 'South Sudan' },
			{ id: 'SD', label: 'Sudan' },
			{ id: 'TZ', label: 'Tanzania' },
			{ id: 'TG', label: 'Togo' },
			{ id: 'TN', label: 'Tunisia' },
			{ id: 'UG', label: 'Uganda' },
			{ id: 'ZM', label: 'Zambia' },
			{ id: 'ZW', label: 'Zimbabwe' }
		]
	},
	{
		id: 'asia',
		label: 'Asia',
		countries: [
			{ id: 'AF', label: 'Afghanistan' },
			{ id: 'AM', label: 'Armenia' },
			{ id: 'AZ', label: 'Azerbaijan' },
			{ id: 'BH', label: 'Bahrain' },
			{ id: 'BD', label: 'Bangladesh' },
			{ id: 'BT', label: 'Bhutan' },
			{ id: 'BN', label: 'Brunei' },
			{ id: 'KH', label: 'Cambodia' },
			{ id: 'CN', label: 'China' },
			{ id: 'CY', label: 'Cyprus' },
			{ id: 'GE', label: 'Georgia' },
			{ id: 'IN', label: 'India' },
			{ id: 'ID', label: 'Indonesia' },
			{ id: 'IR', label: 'Iran' },
			{ id: 'IQ', label: 'Iraq' },
			{ id: 'IL', label: 'Israel' },
			{ id: 'JP', label: 'Japan' },
			{ id: 'JO', label: 'Jordan' },
			{ id: 'KZ', label: 'Kazakhstan' },
			{ id: 'KW', label: 'Kuwait' },
			{ id: 'KG', label: 'Kyrgyzstan' },
			{ id: 'LA', label: 'Laos' },
			{ id: 'LB', label: 'Lebanon' },
			{ id: 'MY', label: 'Malaysia' },
			{ id: 'MV', label: 'Maldives' },
			{ id: 'MN', label: 'Mongolia' },
			{ id: 'MM', label: 'Myanmar (Burma)' },
			{ id: 'NP', label: 'Nepal' },
			{ id: 'KP', label: 'North Korea' },
			{ id: 'OM', label: 'Oman' },
			{ id: 'PK', label: 'Pakistan' },
			{ id: 'PS', label: 'Palestinian Territories' },
			{ id: 'PH', label: 'Philippines' },
			{ id: 'QA', label: 'Qatar' },
			{ id: 'SA', label: 'Saudi Arabia' },
			{ id: 'SG', label: 'Singapore' },
			{ id: 'KR', label: 'South Korea' },
			{ id: 'LK', label: 'Sri Lanka' },
			{ id: 'SY', label: 'Syria' },
			{ id: 'TW', label: 'Taiwan' },
			{ id: 'TJ', label: 'Tajikistan' },
			{ id: 'TH', label: 'Thailand' },
			{ id: 'TL', label: 'Timor-Leste' },
			{ id: 'TR', label: 'Türkiye' },
			{ id: 'TM', label: 'Turkmenistan' },
			{ id: 'AE', label: 'United Arab Emirates' },
			{ id: 'UZ', label: 'Uzbekistan' },
			{ id: 'VN', label: 'Vietnam' },
			{ id: 'YE', label: 'Yemen' }
		]
	},
	{
		id: 'europe',
		label: 'Europe',
		countries: [
			{ id: 'AL', label: 'Albania' },
			{ id: 'AD', label: 'Andorra' },
			{ id: 'AT', label: 'Austria' },
			{ id: 'BY', label: 'Belarus' },
			{ id: 'BE', label: 'Belgium' },
			{ id: 'BA', label: 'Bosnia & Herzegovina' },
			{ id: 'BG', label: 'Bulgaria' },
			{ id: 'HR', label: 'Croatia' },
			{ id: 'CZ', label: 'Czechia' },
			{ id: 'DK', label: 'Denmark' },
			{ id: 'EE', label: 'Estonia' },
			{ id: 'FI', label: 'Finland' },
			{ id: 'FR', label: 'France' },
			{ id: 'DE', label: 'Germany' },
			{ id: 'GR', label: 'Greece' },
			{ id: 'HU', label: 'Hungary' },
			{ id: 'IS', label: 'Iceland' },
			{ id: 'IE', label: 'Ireland' },
			{ id: 'IT', label: 'Italy' },
			{ id: 'LV', label: 'Latvia' },
			{ id: 'LI', label: 'Liechtenstein' },
			{ id: 'LT', label: 'Lithuania' },
			{ id: 'LU', label: 'Luxembourg' },
			{ id: 'MT', label: 'Malta' },
			{ id: 'MD', label: 'Moldova' },
			{ id: 'MC', label: 'Monaco' },
			{ id: 'ME', label: 'Montenegro' },
			{ id: 'NL', label: 'Netherlands' },
			{ id: 'MK', label: 'North Macedonia' },
			{ id: 'NO', label: 'Norway' },
			{ id: 'PL', label: 'Poland' },
			{ id: 'PT', label: 'Portugal' },
			{ id: 'RO', label: 'Romania' },
			{ id: 'RU', label: 'Russia' },
			{ id: 'SM', label: 'San Marino' },
			{ id: 'RS', label: 'Serbia' },
			{ id: 'SK', label: 'Slovakia' },
			{ id: 'SI', label: 'Slovenia' },
			{ id: 'ES', label: 'Spain' },
			{ id: 'SE', label: 'Sweden' },
			{ id: 'CH', label: 'Switzerland' },
			{ id: 'UA', label: 'Ukraine' },
			{ id: 'GB', label: 'United Kingdom' },
			{ id: 'VA', label: 'Vatican City' }
		]
	},
	{
		id: 'north-america',
		label: 'North America',
		countries: [
			{ id: 'AG', label: 'Antigua & Barbuda' },
			{ id: 'BS', label: 'Bahamas' },
			{ id: 'BB', label: 'Barbados' },
			{ id: 'BZ', label: 'Belize' },
			{ id: 'CA', label: 'Canada' },
			{ id: 'CR', label: 'Costa Rica' },
			{ id: 'CU', label: 'Cuba' },
			{ id: 'DM', label: 'Dominica' },
			{ id: 'DO', label: 'Dominican Republic' },
			{ id: 'SV', label: 'El Salvador' },
			{ id: 'GD', label: 'Grenada' },
			{ id: 'GT', label: 'Guatemala' },
			{ id: 'HT', label: 'Haiti' },
			{ id: 'HN', label: 'Honduras' },
			{ id: 'JM', label: 'Jamaica' },
			{ id: 'MX', label: 'Mexico' },
			{ id: 'NI', label: 'Nicaragua' },
			{ id: 'PA', label: 'Panama' },
			{ id: 'KN', label: 'St. Kitts & Nevis' },
			{ id: 'LC', label: 'St. Lucia' },
			{ id: 'VC', label: 'St. Vincent & Grenadines' },
			{ id: 'TT', label: 'Trinidad & Tobago' },
			{ id: 'US', label: 'United States' }
		]
	},
	{
		id: 'oceania',
		label: 'Oceania',
		countries: [
			{ id: 'AU', label: 'Australia' },
			{ id: 'FJ', label: 'Fiji' },
			{ id: 'KI', label: 'Kiribati' },
			{ id: 'MH', label: 'Marshall Islands' },
			{ id: 'FM', label: 'Micronesia' },
			{ id: 'NR', label: 'Nauru' },
			{ id: 'NZ', label: 'New Zealand' },
			{ id: 'PW', label: 'Palau' },
			{ id: 'PG', label: 'Papua New Guinea' },
			{ id: 'WS', label: 'Samoa' },
			{ id: 'SB', label: 'Solomon Islands' },
			{ id: 'TO', label: 'Tonga' },
			{ id: 'TV', label: 'Tuvalu' },
			{ id: 'VU', label: 'Vanuatu' }
		]
	},
	{
		id: 'south-america',
		label: 'South America',
		countries: [
			{ id: 'AR', label: 'Argentina' },
			{ id: 'BO', label: 'Bolivia' },
			{ id: 'BR', label: 'Brazil' },
			{ id: 'CL', label: 'Chile' },
			{ id: 'CO', label: 'Colombia' },
			{ id: 'EC', label: 'Ecuador' },
			{ id: 'GY', label: 'Guyana' },
			{ id: 'PY', label: 'Paraguay' },
			{ id: 'PE', label: 'Peru' },
			{ id: 'SR', label: 'Suriname' },
			{ id: 'UY', label: 'Uruguay' },
			{ id: 'VE', label: 'Venezuela' }
		]
	}
]

/**
 * The two directions a country criterion travels: a stored ISO code out to the name
 * the drawer shows, and any name the product spells back to its code.
 *
 * Two lists describe the same countries and disagree on eighteen: `config/countries`
 * is the product's (and the source of stored codes), while the labels above come from
 * `Intl.DisplayNames` and read the modern way (`Czechia` vs `Czech Republic`).
 * Off `config/countries` alone neither direction round-trips — writing dropped the
 * name, and a criterion with `values: []` makes the engine SKIP the WHOLE rule.
 *
 * So: names out prefer the picker's spelling; names in accept BOTH. Codes the picker
 * does not carry keep the product's name, so an edit that never opens it round-trips.
 */
export const countryNameByCode = new Map<string, string>(
	CountriesList.map((country) => [country.code, country.name] as const)
)
export const countryCodeByName = new Map<string, string>(
	CountriesList.map((country) => [country.name, country.code] as const)
)
CONTINENTS.forEach((continent) => {
	continent.countries.forEach((country) => {
		countryNameByCode.set(country.id, country.label)
		countryCodeByName.set(country.label, country.id)
	})
})

export interface CountryGroup {
	/** A continent when `whole` is set, otherwise one country. */
	label: string
	whole: boolean
}

/**
 * Picked country names folded back onto the continents they fill, always in
 * CONTINENTS order. A name this list does not carry is still named, last and on its
 * own — the 54 territories and the legacy `Global` are real values the engine
 * matches, and dropping them would understate what the rule targets.
 */
export const groupCountryNames = (names: string[]): CountryGroup[] => {
	const picked = new Set(names)
	const groups: CountryGroup[] = []
	const placed = new Set<string>()
	CONTINENTS.forEach((continent) => {
		const inside = continent.countries.filter((country) =>
			picked.has(country.label)
		)
		if (!inside.length) return
		inside.forEach((country) => placed.add(country.label))
		if (inside.length === continent.countries.length) {
			groups.push({ label: continent.label, whole: true })
			return
		}
		inside.forEach((country) =>
			groups.push({ label: country.label, whole: false })
		)
	})
	// Input order among themselves — there is no continent to sort them by, and
	// the order they arrived in is the order the rule stored them.
	names.forEach((name) => {
		if (placed.has(name)) return
		placed.add(name)
		groups.push({ label: name, whole: false })
	})
	return groups
}
